import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell,
} from 'recharts';
import { CpuChipIcon, SparklesIcon, ShareIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { API_CONFIG } from '../config/api';


const MiniCard = ({ title, subtitle, children }) => (
  <div className="rounded-2xl p-5 flex flex-col border border-indigo-200 shadow-sm relative overflow-hidden"
    style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #eef2ff 60%, #e0e7ff 100%)' }}>
    {/* Dot contour pattern */}
    <div className="absolute inset-0 pointer-events-none" style={{
      backgroundImage: 'radial-gradient(circle, #a5b4fc 1px, transparent 1px)',
      backgroundSize: '18px 18px',
      opacity: 0.4,
    }} />
    <div className="relative z-10 mb-3">
      <p className="text-sm font-bold text-gray-800 leading-none tracking-tight">{title}</p>
      <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
    </div>
    <div className="relative z-10 flex flex-col flex-1">{children}</div>
  </div>
);

export default function HomeStatsRow() {
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_CONFIG.API_BASE}/dashboard/home-stats`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => {
      if (r.data.success) {
        setStats(r.data.stats);
        setDaily(r.data.daily || []);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Pie: platform asset composition from stats counts
  const assetPie = stats ? [
    { name: 'Workflows',      value: stats.workflows     || 0, color: '#818cf8' },
    { name: 'Cognix Agents',  value: stats.cognix_agents || 0, color: '#c084fc' },
    { name: 'DB Agents',      value: stats.db_agents     || 0, color: '#34d399' },
    { name: 'Excel Agents',   value: stats.excel_agents  || 0, color: '#fb923c' },
  ] : [];

  const spinnerDark = (
    <div className="flex-1 flex items-center justify-center h-24">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" />
    </div>
  );

  const axisProps = { axisLine: false, tickLine: false, tick: { fontSize: 11, fill: '#64748b' } };

  return (
    <div className="space-y-3">
      {/* 4 mini chart cards in one row */}
      <div className="grid grid-cols-4 gap-3">

        {/* 1. Token Trend — Area chart of daily token consumption */}
        <MiniCard title="Token Trend" subtitle="Daily AI token consumption">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: '#fbbf24' }} />
            <span className="text-xs font-medium" style={{ color: '#d97706' }}>Tokens / day</span>
          </div>
          {loading ? spinnerDark : (
            <ResponsiveContainer width="100%" height={128}>
              <AreaChart data={daily} margin={{ top: 4, right: 4, left: -26, bottom: 0 }}>
                <defs>
                  <linearGradient id="tokenAreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fbbf24" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#fbbf24" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" {...axisProps} />
                <YAxis {...axisProps} allowDecimals={false}
                  tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                <Tooltip content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const v = payload[0].value;
                  return (
                    <div style={{ background: '#fff', border: '1px solid #e0e7ff', borderRadius: 8, padding: '6px 12px', fontSize: 11 }}>
                      <p style={{ color: '#64748b', marginBottom: 2 }}>{label}</p>
                      <p style={{ color: '#f59e0b' }}>Tokens: <strong>{v >= 1000 ? `${(v/1000).toFixed(1)}K` : v}</strong></p>
                    </div>
                  );
                }} />
                <Area type="monotone" dataKey="tokens" stroke="#fbbf24" strokeWidth={2.5}
                  fill="url(#tokenAreaGrad)" dot={false} activeDot={{ r: 5, fill: '#fbbf24', strokeWidth: 0 }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </MiniCard>

        {/* 2. Workflow Executions — Bar chart of daily workflow runs */}
        <MiniCard title="Workflow Executions" subtitle="Workflow runs per day">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#6366f1' }} />
            <span className="text-xs font-medium" style={{ color: '#4f46e5' }}>Runs / day</span>
          </div>
          {loading ? spinnerDark : (
            <ResponsiveContainer width="100%" height={128}>
              <BarChart data={daily} barSize={20} margin={{ top: 4, right: 4, left: -26, bottom: 0 }}>
                <XAxis dataKey="day" {...axisProps} />
                <YAxis {...axisProps} allowDecimals={false} />
                <Tooltip content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div style={{ background: '#fff', border: '1px solid #e0e7ff', borderRadius: 8, padding: '6px 12px', fontSize: 11 }}>
                      <p style={{ color: '#64748b', marginBottom: 2 }}>{label}</p>
                      <p style={{ color: '#4f46e5' }}>Runs: <strong>{payload[0].value}</strong></p>
                    </div>
                  );
                }} />
                <defs>
                  <linearGradient id="wfGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" />
                    <stop offset="100%" stopColor="#4f46e5" stopOpacity={0.7} />
                  </linearGradient>
                </defs>
                <Bar dataKey="workflow" name="Runs" fill="url(#wfGrad)" radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </MiniCard>

        {/* 3. Platform Assets — Donut of what the user has built */}
        <MiniCard title="Platform Assets" subtitle="Your AI resources built">
          {loading ? spinnerDark : (
            <div className="flex flex-col flex-1">
              <ResponsiveContainer width="100%" height={110}>
                <PieChart>
                  <Pie data={assetPie} cx="50%" cy="50%"
                    innerRadius={32} outerRadius={50} paddingAngle={4} dataKey="value"
                    strokeWidth={0}>
                    {assetPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div style={{ background: '#fff', border: '1px solid #e0e7ff', borderRadius: 8, padding: '6px 12px', fontSize: 11 }}>
                        <p style={{ color: payload[0].payload.color, fontWeight: 600 }}>{payload[0].name}: {payload[0].value}</p>
                      </div>
                    );
                  }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 space-y-1.5">
                {assetPie.map(d => (
                  <div key={d.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: d.color }} />
                      <span className="text-xs text-gray-600">{d.name}</span>
                    </div>
                    <span className="text-xs font-bold text-gray-800">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </MiniCard>

        {/* 4. Cognix Agent Runs — Area chart of daily Cognix executions */}
        <MiniCard title="Cognix Executions" subtitle="Agent runs per day">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: '#a78bfa' }} />
            <span className="text-xs font-medium" style={{ color: '#7c3aed' }}>Runs / day</span>
          </div>
          {loading ? spinnerDark : (
            <ResponsiveContainer width="100%" height={128}>
              <AreaChart data={daily} margin={{ top: 4, right: 4, left: -26, bottom: 0 }}>
                <defs>
                  <linearGradient id="cxAreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#a78bfa" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" {...axisProps} />
                <YAxis {...axisProps} allowDecimals={false} />
                <Tooltip content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div style={{ background: '#fff', border: '1px solid #e0e7ff', borderRadius: 8, padding: '6px 12px', fontSize: 11 }}>
                      <p style={{ color: '#64748b', marginBottom: 2 }}>{label}</p>
                      <p style={{ color: '#7c3aed' }}>Runs: <strong>{payload[0].value}</strong></p>
                    </div>
                  );
                }} />
                <Area type="monotone" dataKey="cognix" stroke="#a78bfa" strokeWidth={2.5}
                  fill="url(#cxAreaGrad)" dot={false} activeDot={{ r: 5, fill: '#a78bfa', strokeWidth: 0 }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </MiniCard>


      </div>
    </div>
  );
}
